import React, { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { MediumText, RegularText } from '../../lib/Text';
import { formatReportNumber } from './sessionReportPresentation';
import { isBeforeMembershipIssue } from './SessionReportBeforeIssue';
import { useAccessMode } from '@/context/AccessModeContext';
import DialogShell from './components/exceptionRates/DialogShell';
import { getSessionReportLessons, postSessionReportLessonMove } from '../../features/sessionReport/api';
import { sessionReportKeys } from '../../features/sessionReport/keys';
import { formatKst } from '../../features/sessionReport/labels';
import {
    buildMoveWarnings,
    describeMoveError,
    describeNotMovable,
    describeUnavailableTarget,
    formatLessonTime,
    invalidateAfterLessonMove,
    newMoveId,
    resolveUnknownMove,
    UNVERIFIED_ORIGIN_CHECK,
    UNVERIFIED_ORIGIN_NOTICE,
} from '../../features/sessionReport/lessonMove';
import {
    LessonMoveResult,
    SessionReportLesson,
    SessionReportLessonTarget,
    SessionReportMembership,
} from '../../features/sessionReport/types';

/**
 * 세션 현황 회원 창의 수강권 카드 아래 「수업 보기」 — 수업을 다른 수강권으로 옮긴다.
 * 명세 docs/SESSION_REPORT_LESSON_MOVE_PLAN.md §4-8. 서버가 준 것만 보여 준다(다른 센터 흔적·이유 추측 금지).
 */

const ATTENDANCE_LABEL: Record<string, string> = { ATTENDANCE: '출석', NOSHOW: '노쇼', ABSENCE: '노쇼' };
const isReservedLesson = (lesson: SessionReportLesson) => lesson.sessionNumber !== undefined && dayjs(lesson.startTime).isAfter(dayjs());

const formatTargetDate = (date: string) => /^\d{4}-\d{2}-\d{2}$/.test(date)
    ? dayjs.tz(date, 'Asia/Seoul').format('YYYY-MM-DD')
    : formatKst(date, 'YYYY-MM-DD');

type Notice = { tone: 'ok' | 'warn'; text: string } | null;

export type LessonMoveCompletion = {
    notice: NonNullable<Notice>;
    lessonTime: string;
    targetName: string;
    recorded: boolean;
};

/** 기간: 기본은 고른 달. 마이너스·발급 전 수업 카드는 원인이 다른 달에 있을 수 있어 전 기간. */
type MoveSourceMembership = Pick<SessionReportMembership, 'membershipId' | 'name'>
    & Partial<Pick<SessionReportMembership, 'startAt' | 'remaining' | 'flags' | 'totalSession'>>;

const rangeOf = (m: MoveSourceMembership, yearMonth?: string) => {
    if (!yearMonth) return { from: '', to: '' };
    const wide = (m.flags ?? []).some((f) => f === 'NEGATIVE_REMAINING' || f === 'SCHEDULE_BEFORE_ISSUE' || f === 'LESSON_BEFORE_START');
    if (wide) return { from: '', to: '' };
    const start = dayjs.tz(`${yearMonth}-01`, 'Asia/Seoul');
    return { from: start.toISOString(), to: start.add(1, 'month').toISOString() };
};

export const LessonMovePanel = ({
    centerId,
    membership,
    yearMonth,
    readOnly = false,
    onDone,
}: {
    centerId: string;
    membership: MoveSourceMembership;
    /** 없으면 전 기간(회원 수강권 관리 화면) */
    yearMonth?: string;
    /** 화면 쪽 읽기 전용(탈퇴 회원·종료 수강권 등). 열람 세션 판정과 함께 버튼을 숨긴다. */
    readOnly?: boolean;
    /** 완료 안내를 바깥 상세 창에도 남긴다. 기록 저장 대기·실패 중에는 호출하지 않는다. */
    onDone?: (completion: LessonMoveCompletion) => void;
}) => {
    const { isReadOnly: viewOnly } = useAccessMode();
    const isReadOnly = viewOnly || readOnly;
    const queryClient = useQueryClient();
    const { from, to } = rangeOf(membership, yearMonth);
    const query = useInfiniteQuery({
        queryKey: sessionReportKeys.lessons(centerId, membership.membershipId, from, to),
        // React Query 4 — 첫 쪽은 pageParam 이 없다.
        queryFn: ({ pageParam }: { pageParam?: string }) =>
            getSessionReportLessons({ centerId, membershipId: membership.membershipId, from, to, cursor: pageParam ?? null }),
        getNextPageParam: (last) => (last.status === 'ok' ? last.data.nextCursor ?? undefined : undefined),
        enabled: !!centerId && !!membership.membershipId,
        retry: false,
        refetchOnWindowFocus: false,
    });
    const [pickFor, setPickFor] = useState<string | null>(null);
    // 확인 창은 연 순간의 센터·원본 수강권·수업·대상을 함께 들고 있다(표시와 전송이 같은 원본을 쓰게)
    const [confirm, setConfirm] = useState<{
        centerId: string;
        source: MoveSourceMembership;
        lesson: SessionReportLesson;
        target: SessionReportLessonTarget;
    } | null>(null);
    // 같은 패널이 다른 수강권·센터로 바뀌면(뒤로가기 등) 고르기 목록만 접는다. 확인 창은 버리지 않는다 — 연 순간의 원본을
    // 들고 있어 표시와 전송이 같고, 결과 모름의 「결과 확인」처럼 끝나지 않은 요청을 이어갈 수 있어야 한다(검증 3회차).
    useEffect(() => {
        setPickFor(null);
    }, [centerId, membership.membershipId]);
    const [notice, setNotice] = useState<Notice>(null);

    const pages = useMemo(() => query.data?.pages ?? [], [query.data]);
    const serverUnavailable = pages.some((p) => p.status === 'unavailable');
    const lessons = useMemo(() => pages.flatMap((p) => (p.status === 'ok' ? p.data.lessons : [])), [pages]);
    const targets = useMemo(() => {
        const first = pages.find((p) => p.status === 'ok');
        return first && first.status === 'ok' ? first.data.targets : [];
    }, [pages]);
    // 후보에서 빠진 같은 회원 수강권과 이유 — 「왜 후보에 없지」에 답한다. 모르는 이유는 싣지 않는다.
    const unavailable = useMemo(() => {
        const first = pages.find((p) => p.status === 'ok');
        const list = first && first.status === 'ok' ? first.data.unavailableTargets ?? [] : [];
        return list.flatMap((t) => {
            const text = describeUnavailableTarget(t.reason);
            return text ? [{ ...t, text }] : [];
        });
    }, [pages]);
    const [showUnavailable, setShowUnavailable] = useState(false);

    const afterChange = (text: Notice, recorded = false) => {
        if (text && confirm) onDone?.({ notice: text, lessonTime: formatLessonTime(confirm.lesson.startTime), targetName: confirm.target.name, recorded });
        setNotice(text);
        setPickFor(null);
        setConfirm(null);
        invalidateAfterLessonMove(queryClient);
    };

    // 확인 창은 목록 상태(불러오는 중·오류)와 무관하게 유지한다 — 원본이 바뀌어 새 목록을 불러오는 동안에도 끝나지 않은 요청을 잇는다
    const dialog = confirm && (
        <LessonMoveConfirm
            key={`${confirm.centerId}:${confirm.source.membershipId}:${confirm.lesson.scheduleId}:${confirm.target.membershipId}`}
            centerId={confirm.centerId}
            source={confirm.source}
            lesson={confirm.lesson}
            target={confirm.target}
            onClose={() => setConfirm(null)}
            onDone={afterChange}
            onReload={() => {
                query.refetch();
            }}
        />
    );

    // 확인 창은 언제나 같은 자리(바깥 묶음의 두 번째 자식)에 그린다. 목록 상태에 따라 자리가 바뀌면 창이 새로 만들어져
    // 옮기기 번호·진행 단계·체크가 초기화된다(검증 4회차).
    let body: React.ReactNode;
    if (query.isLoading) {
        body = <p className="mt-[10px] text-[14px] text-[#4E5968]">수업을 불러오고 있어요…</p>;
    } else if (query.isError) {
        body = (
            <p className="mt-[10px] text-[14px] text-[#B91C1C]">
                수업을 불러오지 못했어요.{' '}
                <button type="button" className="underline underline-offset-4" onClick={() => query.refetch()}>
                    다시 불러오기
                </button>
            </p>
        );
    } else if (serverUnavailable) {
        body = <p className="mt-[10px] text-[14px] text-[#4E5968]">지금은 이 수강권의 수업을 볼 수 없어요.</p>;
    } else {
        body = renderList();
    }
    return (
        <>
            {body}
            {dialog}
        </>
    );

    function renderList() {
        const selectedLesson = lessons.find((lesson) => lesson.scheduleId === pickFor);
        const selectedTarget = confirm?.lesson.scheduleId === pickFor ? confirm.target : null;
        return (
        <div className="mt-[16px] min-w-0 rounded-[12px] bg-[#F2F4F6] px-[14px] py-[16px]">
            {!isReadOnly && selectedLesson && <div role="group" aria-label="수업 옮기기 순서" className="mb-[14px] grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-stretch gap-[10px]">
                <div className={`flex min-w-0 flex-col justify-center gap-[5px] rounded-[10px] border-2 p-[14px] ${selectedLesson ? 'border-solid border-[#A6BFEF] bg-white' : 'border-dashed border-[#A5ABB5]'}`}>
                    <RegularText fontSize={14} lineHeight={19} color="#4E5968">옮길 수업</RegularText>
                    {selectedLesson ? <>
                        <MediumText fontSize={15} lineHeight={22} color="#3D4244">{formatLessonTime(selectedLesson.startTime)}</MediumText>
                        {isReservedLesson(selectedLesson) ? <span className="text-[14px] text-[#4E5968]">예약</span>
                            : selectedLesson.attendanceStatus && ATTENDANCE_LABEL[selectedLesson.attendanceStatus] && <MediumText fontSize={14} lineHeight={19} color="#2B529D">{ATTENDANCE_LABEL[selectedLesson.attendanceStatus]}</MediumText>}
                        <RegularText fontSize={14} lineHeight={19} color="#4E5968" className="break-words">{membership.name}</RegularText>
                    </> : <MediumText fontSize={14} lineHeight={21} color="#4E5968">수업을 고르세요</MediumText>}
                </div>
                <span aria-hidden="true" className="self-center text-[22px] text-[#4E5968]">→</span>
                <div className={`flex min-w-0 flex-col justify-center gap-[5px] rounded-[10px] border-2 p-[14px] ${selectedTarget ? 'border-solid border-[#A6BFEF] bg-white' : 'border-dashed border-[#A5ABB5]'}`}>
                    <MediumText fontSize={14} lineHeight={21} color="#4E5968">받을 수강권</MediumText>
                    {selectedTarget ? <>
                        <MediumText fontSize={15} lineHeight={22} color="#3D4244" className="break-words">{selectedTarget.name}</MediumText>
                        <RegularText fontSize={14} lineHeight={19} color="#4E5968">{`남은 회차 ${formatReportNumber(selectedTarget.bookableRemaining)}회`}</RegularText>
                    </> : selectedLesson && <RegularText fontSize={14} lineHeight={19} color="#4E5968">아래에서 골라 주세요</RegularText>}
                </div>
            </div>}
            <p className="text-[14px] leading-[20px] text-[#4E5968]">
                {from ? `${dayjs(from).tz('Asia/Seoul').format('M월')} 수업이에요.` : '이 수강권의 모든 수업이에요.'}
                {!isReadOnly && ' 잘못 차감된 수업은 「다른 수강권으로」를 눌러 옮길 수 있어요.'}
            </p>
            {notice && (
                <p className={`mt-[8px] text-[14px] leading-[20px] ${notice.tone === 'ok' ? 'text-[#15803D]' : 'text-[#B45309]'}`}>{notice.text}</p>
            )}
            {lessons.length === 0 ? (
                <p className="mt-[8px] text-[14px] text-[#4E5968]">이 기간에 수업이 없어요.</p>
            ) : (
                <ul className="mt-[10px] flex flex-col gap-[8px]">
                    {lessons.map((lesson) => {
                        const candidates = targets.filter((t) => lesson.eligibleTargetIds.includes(t.membershipId));
                        const open = pickFor === lesson.scheduleId;
                        const hasSessionNumber = lesson.sessionNumber !== undefined;
                        // 구서버의 행은 그대로 유지한다. 미래 수업도 서버가 준 번호를 그대로 쓴다.
                        const reserved = isReservedLesson(lesson);
                        return (
                            <li key={lesson.scheduleId} className={`min-w-0 rounded-[10px] border border-solid p-[12px] ${open ? 'border-[#A6BFEF] bg-[#F4F7FF]' : 'border-[#E5E8EB] bg-white'}`}>
                                <div className="flex flex-wrap items-center gap-[10px] text-[14px] leading-[20px]">
                                    {hasSessionNumber && <span aria-label="수업 회차" className="font-semibold tabular-nums" style={{ color: reserved ? '#4E5968' : '#333D4B' }}>
                                        {lesson.sessionNumber == null ? '-' : `${formatReportNumber(lesson.sessionNumber)}회차`}
                                    </span>}
                                    <span className={reserved ? 'text-[#4E5968]' : 'text-[#333D4B]'}>{formatLessonTime(lesson.startTime)}</span>
                                    {isBeforeMembershipIssue(lesson.startTime, membership.startAt) && <span className="rounded-[4px] bg-[#F2F4F6] px-[6px] text-[12px] text-[#4E5968]">발급 전 수업</span>}
                                    {reserved ? <span className="text-[14px] text-[#4E5968]">예약</span> : lesson.attendanceStatus && ATTENDANCE_LABEL[lesson.attendanceStatus] && (
                                        <span className="text-[14px] text-[#4E5968]">{ATTENDANCE_LABEL[lesson.attendanceStatus]}</span>
                                    )}
                                    {lesson.isOtherCoach && <span className="rounded-[4px] bg-[#F2F4F6] px-[6px] text-[14px] text-[#4E5968]">대신 들어감{lesson.coachLabel && ` · ${lesson.coachLabel}`}</span>}
                                    <span className="ml-auto" />
                                    {!lesson.movable ? (
                                        <span className="text-[14px] text-[#4E5968]">{describeNotMovable(lesson.notMovableReason)}</span>
                                    ) : isReadOnly ? null : candidates.length === 0 ? (
                                        <span className="text-[14px] text-[#4E5968]">옮길 수 있는 수강권이 없어요</span>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setPickFor(open ? null : lesson.scheduleId)}
                                            aria-expanded={open}
                                            className="min-h-[40px] px-[10px] rounded-[7px] border border-solid border-[#D9E6FF] bg-white text-[14px] text-primary hover:bg-[#F4F7FF]"
                                        >
                                            {open ? '닫기' : '다른 수강권으로'}
                                        </button>
                                    )}
                                </div>
                                {!isReadOnly && open && (
                                    <div className="mt-[14px]">
                                        <MediumText fontSize={14} lineHeight={22} color="#3D4244">받을 수강권을 골라 주세요</MediumText>
                                        <div className="mt-[8px] flex flex-wrap gap-[8px]">
                                        {candidates.map((t) => (
                                            <button
                                                key={t.membershipId}
                                                type="button"
                                                // 확인 창이 떠 있는 동안에는 뒤쪽 후보를 바꿀 수 없다(키보드 포함) — 같은 옮기기 번호로 다른 대상이 가지 않게
                                                disabled={!t.selectable || confirm !== null}
                                                onClick={() => setConfirm((current) => current ?? { centerId, source: membership, lesson, target: t })}
                                                className="flex min-w-0 max-w-full grow basis-[240px] flex-col items-start gap-[8px] rounded-[10px] border border-solid border-[#D9E6FF] bg-white p-[14px] text-left text-[14px] enabled:hover:border-primary enabled:hover:bg-[#F4F7FF] disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <MediumText fontSize={15} lineHeight={22} color="#3D4244" className="break-words">{t.name}</MediumText>
                                                <RegularText fontSize={13} lineHeight={19} color="#4E5968" className="break-keep">
                                                    {[
                                                        t.issueAt ? `${formatTargetDate(t.issueAt)} 발급` : '발급일 확인 필요',
                                                        t.expiryPolicy === 'COUNT' ? '기간 지나도 사용 가능' : t.expirationDate
                                                            ? `${formatTargetDate(t.expirationDate)} 만료`
                                                            : '만료일 확인 필요',
                                                    ].join(' · ')}
                                                </RegularText>
                                                <RegularText fontSize={14} lineHeight={19} color="#4E5968">
                                                    {t.selectable ? `남은 회차 ${formatReportNumber(t.bookableRemaining)}회` : '남은 회차가 없어요'}
                                                </RegularText>
                                                {t.isExpired && <span className="rounded-[4px] bg-WARNING_50 px-[6px] text-[14px] text-WARNING">만료된 수강권</span>}
                                                {t.zeroPrice && <span className="rounded-[4px] bg-[#F2F4F6] px-[6px] text-[14px] text-[#4E5968]">판매 0원</span>}
                                                {t.originVerified === false && (
                                                    <span className="rounded-[4px] bg-[#FFF7ED] px-[6px] text-[14px] text-[#B45309]">발급 기록 확인 필요</span>
                                                )}
                                            </button>
                                        ))}
                                        </div>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
            {unavailable.length > 0 && (
                <div className="mt-[8px]">
                    <button
                        type="button"
                        onClick={() => setShowUnavailable((v) => !v)}
                        className="text-[14px] text-[#4E5968] underline underline-offset-4"
                    >
                        {showUnavailable ? '접기' : `옮길 수 없는 수강권 ${unavailable.length}개 · 이유 보기`}
                    </button>
                    {showUnavailable && (
                        <ul className="mt-[6px] flex flex-col gap-[4px]">
                            {unavailable.map((t) => (
                                <li key={t.membershipId} className="text-[14px] leading-[18px] text-[#4E5968] break-keep">
                                    {`『${t.name}』${t.isExpired ? '(만료)' : ''} — ${t.text}`}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
            {query.hasNextPage && (
                <button
                    type="button"
                    disabled={query.isFetchingNextPage}
                    onClick={() => query.fetchNextPage()}
                    className="mt-[6px] text-[14px] text-primary underline underline-offset-4"
                >
                    {query.isFetchingNextPage ? '불러오는 중…' : '수업 더 보기'}
                </button>
            )}
        </div>
        );
    }
};

/** 결과 모름일 때 이 수업이 지금 어느 수강권에 있는지 서버 목록으로 확인한다(수업 시각 앞뒤 1시간만). */
const locateLesson = async (centerId: string, lesson: SessionReportLesson, candidates: string[]) => {
    const from = dayjs(lesson.startTime).subtract(1, 'hour').toISOString();
    const to = dayjs(lesson.startTime).add(1, 'hour').toISOString();
    for (const membershipId of candidates) {
        const res = await getSessionReportLessons({ centerId, membershipId, from, to });
        if (res.status === 'ok' && res.data.lessons.some((l) => l.scheduleId === lesson.scheduleId)) return membershipId;
    }
    return null;
};

type Phase =
    | { kind: 'confirm' }
    | { kind: 'sending' }
    | { kind: 'error'; text: string }
    | { kind: 'askAtSource' }
    | { kind: 'logPending' };

const LessonMoveConfirm = ({
    centerId,
    source,
    lesson,
    target,
    onClose,
    onDone,
    onReload,
}: {
    centerId: string;
    source: MoveSourceMembership;
    lesson: SessionReportLesson;
    target: SessionReportLessonTarget;
    onClose: () => void;
    onDone: (notice: Notice, recorded?: boolean) => void;
    onReload: () => void;
}) => {
    // 창을 열 때 한 번 만든다. 같은 창에서의 재확인은 같은 번호, 다시 옮기려면 새 창(새 번호).
    const [moveId] = useState(newMoveId);
    const [phase, setPhase] = useState<Phase>({ kind: 'confirm' });
    const warnings = buildMoveWarnings(lesson, target);
    // 발급 기록 확인 안 된 수강권은 관리자가 체크해야 옮길 수 있다(서버도 같은 규칙으로 막는다)
    const needsOriginCheck = target.originVerified === false;
    const [originChecked, setOriginChecked] = useState(false);
    // 보낼 내용은 창을 열 때 고정한다(재확인·기록 다시 저장도 같은 번호·같은 대상). 확인 표시는 실제로 체크했을 때만 붙인다.
    const [baseBody] = useState(() => ({
        moveId,
        scheduleId: lesson.scheduleId,
        expectedSourceMembershipId: source.membershipId,
        expectedStartTime: lesson.startTime,
        targetMembershipId: target.membershipId,
    }));
    const send = () =>
        postSessionReportLessonMove(centerId, needsOriginCheck && originChecked ? { ...baseBody, confirmUnverifiedOrigin: true } : baseBody);

    const queryClient = useQueryClient();
    const invalidateNow = () => invalidateAfterLessonMove(queryClient);

    const handleOk = (result: Extract<LessonMoveResult, { status: 'ok' }>) => {
        // alreadyApplied 는 「예전에 적용됐다」는 증거일 뿐 지금 위치가 아니다 — 지금 위치는 응답의 membershipId 로 본다.
        const now = result.data.membershipId;
        // 최초 제출이든 결과 확인이든 같다 — 지금 위치가 대상이 아니면(되돌아옴·다른 곳으로 감·가려진 null) 옮겼다고 하지 않는다.
        if (now !== target.membershipId) {
            return onDone(
                now === source.membershipId && result.data.alreadyApplied
                    ? { tone: 'warn', text: `이미 옮겼다가 다시 『${source.name}』로 돌아왔어요(다른 곳에서 되돌렸어요).` }
                    : { tone: 'warn', text: '다른 곳에서 바뀌었어요. 목록을 새로 불러왔어요.' },
            );
        }
        if (!result.data.changeLogRecorded) {
            setPhase({ kind: 'logPending' });
            invalidateNow();
            return;
        }
        return onDone({ tone: 'ok', text: `${formatLessonTime(lesson.startTime)} 수업을 『${target.name}』로 옮겼어요.` }, true);
    };
    const handleUnknown = async () => {
        // 조용히 다시 보내지 않는다 — 지금 위치부터 확인한다.
        onReload();
        let where: string | null = null;
        try {
            where = await locateLesson(centerId, lesson, [target.membershipId, source.membershipId]);
        } catch {
            where = null;
        }
        const next = resolveUnknownMove(where, { sourceMembershipId: source.membershipId, targetMembershipId: target.membershipId });
        if (next === 'confirmAtTarget') {
            // 지금 대상에 있다는 것만 알린다. 자동으로 다시 보내지 않는다 — 확인하는 사이 다른 곳에서 되돌렸으면
            // 그 요청이 실제로 다시 옮길 수 있다(배포 판정 1단계 P2). 기록 보충은 서버 배열이 정본이라 없어도 된다.
            return onDone({ tone: 'ok', text: `『${target.name}』로 옮겨져 있어요.` });
        }
        if (next === 'askAtSource') return setPhase({ kind: 'askAtSource' });
        return onDone({ tone: 'warn', text: '다른 곳에서 바뀌었어요. 목록을 새로 불러왔어요.' });
    };

    const run = async (fromAsk = false) => {
        setPhase({ kind: 'sending' });
        const result = await send();
        if (result.status === 'ok') return handleOk(result);
        if (result.status === 'rejected') {
            const { text, reload } = describeMoveError(result.code);
            if (reload) onReload();
            return setPhase({ kind: 'error', text });
        }
        if (fromAsk) return setPhase({ kind: 'error', text: '결과를 아직 확인하지 못했어요. 잠시 뒤 목록을 새로 불러와 확인해 주세요.' });
        return handleUnknown();
    };

    const busy = phase.kind === 'sending';
    return (
        <DialogShell title="수업을 다른 수강권으로 옮길까요?" width={520} onClose={onClose} busy={busy}>
            <div className="px-[24px] pb-[22px] pt-[8px] flex flex-col gap-[14px]">
                <div aria-label="옮길 수업과 수강권" className="grid grid-cols-[1fr_auto_1fr] items-center gap-[12px] rounded-[12px] bg-[#F8F9FB] p-[16px] text-[14px] leading-[22px]">
                    <dl className="min-w-0 break-words">
                        <dt className="text-[14px] text-[#4E5968]">어느 수업을</dt>
                        <dd className="mt-[4px] font-medium text-[#333D4B]">
                            {formatLessonTime(lesson.startTime)}<br />{source.name}
                            <span className="mt-[4px] block text-[14px] leading-[20px] font-normal text-[#4E5968]">
                                {/* 보내는 쪽 남은 회차는 고른 달 말 기준이라 받는 쪽(지금 예약 가능)과 나란히 두지 않는다 */}
                                {source.startAt ? `${formatTargetDate(source.startAt)} 발급` : '발급일 확인 필요'}
                            </span>
                        </dd>
                    </dl>
                    <span aria-hidden="true" className="text-[20px] text-[#4E5968]">→</span>
                    <dl className="min-w-0 break-words">
                        <dt className="text-[14px] text-[#4E5968]">어느 수강권으로</dt>
                        <dd className="mt-[4px] font-medium text-[#333D4B]">
                            {target.name}
                            <span className="mt-[4px] block text-[14px] leading-[20px] font-normal text-[#4E5968]">
                                {`${target.issueAt ? `${formatTargetDate(target.issueAt)} 발급` : '발급일 확인 필요'} · 남은 ${formatReportNumber(target.bookableRemaining)}회`}
                            </span>
                        </dd>
                    </dl>
                </div>
                <p className="text-[15px] leading-[24px] text-[#333D4B] break-keep">
                    {`${formatLessonTime(lesson.startTime)} 수업을 『${source.name}』에서 『${target.name}』로 옮겨요.`}
                </p>
                <ul className="flex flex-col gap-[6px] rounded-[10px] bg-[#FFF7ED] px-[14px] py-[12px]">
                    {warnings.map((w) => (
                        <li key={w} className="text-[14px] leading-[20px] text-[#9A3412] break-keep">
                            · {w}
                        </li>
                    ))}
                </ul>
                {needsOriginCheck && (
                    <div className="rounded-[10px] border border-solid border-[#FCD9B6] px-[14px] py-[12px] flex flex-col gap-[10px]">
                        <p className="text-[14px] leading-[20px] text-[#9A3412] break-keep">{UNVERIFIED_ORIGIN_NOTICE}</p>
                        <label className="flex items-center gap-[8px] text-[14px] text-[#333D4B] cursor-pointer">
                            <input
                                type="checkbox"
                                checked={originChecked}
                                disabled={busy || phase.kind !== 'confirm'}
                                onChange={(e) => setOriginChecked(e.target.checked)}
                                className="h-[16px] w-[16px] accent-primary"
                            />
                            {UNVERIFIED_ORIGIN_CHECK}
                        </label>
                    </div>
                )}
                {phase.kind === 'error' && <p className="text-[14px] leading-[20px] text-[#B91C1C]">{phase.text}</p>}
                {phase.kind === 'askAtSource' && (
                    <p className="text-[14px] leading-[20px] text-[#B45309] break-keep">
                        {`옮기기 결과를 확인하지 못했어요. 지금 이 수업은 『${source.name}』에 있어요. 「결과 확인」을 누르면 한 번 더 확인해요.`}
                    </p>
                )}
                {phase.kind === 'logPending' && (
                    <p className="text-[14px] leading-[20px] text-[#15803D] break-keep">옮겨졌어요. 변경 기록 저장이 늦어지고 있어요.</p>
                )}
                <div className="flex justify-end gap-[8px]">
                    {phase.kind === 'logPending' ? (
                        <>
                            <button type="button" onClick={() => onDone({ tone: 'ok', text: `『${target.name}』로 옮겼어요.` })} className="h-[42px] px-[16px] rounded-[10px] bg-[#F2F4F6] text-[14px] text-[#4E5968]">
                                닫기
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    const again = await send();
                                    if (again.status !== 'ok') return;
                                    // 기록 저장 응답의 지금 위치로 안내한다 — 그 사이 다른 곳에서 되돌렸을 수 있다.
                                    if (again.data.membershipId !== target.membershipId) {
                                        onDone({ tone: 'warn', text: '기록은 확인했어요. 그 사이 다른 곳에서 수업 위치가 바뀌었어요. 목록을 새로 불러왔어요.' });
                                        return;
                                    }
                                    if (again.data.changeLogRecorded) onDone({ tone: 'ok', text: `『${target.name}』로 옮겼고 기록도 저장했어요.` }, true);
                                }}
                                className="h-[42px] px-[16px] rounded-[10px] bg-primary text-[14px] font-semibold text-white"
                            >
                                기록 다시 저장
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" disabled={busy} onClick={onClose} className="h-[42px] px-[16px] rounded-[10px] bg-[#F2F4F6] text-[14px] text-[#4E5968] disabled:opacity-50">
                                {phase.kind === 'error' ? '닫기' : '취소'}
                            </button>
                            {phase.kind === 'askAtSource' ? (
                                <button type="button" onClick={() => run(true)} className="h-[42px] px-[16px] rounded-[10px] bg-primary text-[14px] font-semibold text-white">
                                    결과 확인
                                </button>
                            ) : phase.kind !== 'error' ? (
                                <button type="button" disabled={busy || (needsOriginCheck && !originChecked)} onClick={() => run(false)} className="h-[42px] px-[16px] rounded-[10px] bg-primary text-[14px] font-semibold text-white disabled:opacity-50">
                                    {busy ? '옮기는 중…' : '옮기기'}
                                </button>
                            ) : null}
                        </>
                    )}
                </div>
            </div>
        </DialogShell>
    );
};

export default LessonMovePanel;
